<script lang="ts" setup>
import {
  findBestInStockCombinationForPrimaryGroup,
  findCombinationForSelection,
  findInStockCombinationForVariantValue,
  getActiveDimensionValues,
  getActivePrimaryGroupLabel,
  getInitialMulticardSelection,
  getMulticardPrimaryThumbnailUrl,
  getPrimaryGroupById,
  isVariantValueAvailable,
  isVariantValueInStock,
  normalizeSelectedValues,
  type MulticardSelectedSizeCharts,
  type MulticardSelectedValues,
} from '~/utils/multicard';
import type { MulticardVariantDimension, MulticardVariantDimensionValue } from '~/types/Catalog';
import type { RouteLocationRaw } from 'vue-router';

const { selectedOffer, selectedSizeId } = storeToRefs(useCatalogStore());
const route = useRoute();
const { navigateToCatalogRoutePreservingScroll } = useCatalogScrollRestoration();
let pendingSelectionTarget: RouteLocationRaw | null = null;
let selectionNavigationPromise: Promise<void> | null = null;

async function navigateToLatestSelection(target: RouteLocationRaw) {
  pendingSelectionTarget = target;

  if (!selectionNavigationPromise) {
    selectionNavigationPromise = (async () => {
      while (pendingSelectionTarget) {
        const nextTarget = pendingSelectionTarget;

        pendingSelectionTarget = null;
        await navigateToCatalogRoutePreservingScroll(nextTarget);
      }
    })();
  }

  try {
    await selectionNavigationPromise;
  } finally {
    selectionNavigationPromise = null;
  }
}

const initialSelection = getInitialMulticardSelection(selectedOffer.value);
const selectedGroupId = ref<number | null>(initialSelection.selectedGroupId);
const selectedValues = ref<MulticardSelectedValues>({ ...initialSelection.selectedValues });
const selectedSizeCharts = ref<MulticardSelectedSizeCharts>({ ...initialSelection.selectedSizeCharts });

const primaryGroups = computed(() => selectedOffer.value.primary_groups ?? []);
const inStockPrimaryGroups = computed(() => primaryGroups.value.filter((group) => group.has_balance));
const primaryGroupsForDisplay = computed(() => {
  const selectedGroup = primaryGroups.value.find((group) => group.id === selectedGroupId.value);

  return selectedGroup &&
    !selectedGroup.has_balance &&
    !inStockPrimaryGroups.value.some((group) => group.id === selectedGroup.id)
    ? [...inStockPrimaryGroups.value, selectedGroup]
    : inStockPrimaryGroups.value;
});
const variantDimensions = computed(() => selectedOffer.value.variant_dimensions ?? []);
const variantPropertyVendorIds = computed(() => {
  return variantDimensions.value.map((dimension) => dimension.property_vendor_id);
});
const combinations = computed(() => selectedOffer.value.combinations ?? []);
const selectedPrimaryGroup = computed(() => getPrimaryGroupById(primaryGroupsForDisplay.value, selectedGroupId.value));
const primarySizeChartProp = computed(() => {
  return (
    Object.values(selectedPrimaryGroup.value?.props ?? {}).find((prop) => {
      return prop.property_vendor_id && prop.size_charts?.length;
    }) ?? null
  );
});
const hasPrimaryGroupThumbnails = computed(() => primaryGroupsForDisplay.value.some((group) => group.thumbnail_url));
const primaryGroupTitle = computed(() => {
  const firstGroup = selectedPrimaryGroup.value;
  const firstPropName = firstGroup ? Object.keys(firstGroup.props ?? {})[0] : null;

  return firstPropName && firstPropName !== 'default' ? firstPropName : 'Вариант';
});
const primarySizeCharts = computed(() => primarySizeChartProp.value?.size_charts ?? []);
const primarySizeChartPropertyVendorId = computed(() => primarySizeChartProp.value?.property_vendor_id ?? null);
const selectedPrimarySizeChartId = computed(() => {
  const propertyVendorId = primarySizeChartPropertyVendorId.value;

  if (!propertyVendorId) {
    return null;
  }

  return selectedSizeCharts.value[propertyVendorId] ?? primarySizeChartProp.value?.size_chart_id ?? null;
});

const currentCombination = computed(() => {
  return (
    findCombinationForSelection(combinations.value, selectedGroupId.value, selectedValues.value) ??
    findBestInStockCombinationForPrimaryGroup(combinations.value, selectedGroupId.value, selectedValues.value)
  );
});
const primaryGroupThumbnailUrls = computed<Record<number, string | null>>(() => {
  return Object.fromEntries(
    primaryGroupsForDisplay.value.map((group) => {
      const targetCombination =
        group.id === selectedGroupId.value
          ? currentCombination.value
          : findBestInStockCombinationForPrimaryGroup(combinations.value, group.id, selectedValues.value);

      return [
        group.id,
        getMulticardPrimaryThumbnailUrl(selectedOffer.value, targetCombination?.offer_id ?? null, group.thumbnail_url),
      ];
    }),
  );
});

watch(
  currentCombination,
  (combination) => {
    selectedSizeId.value = combination?.offer_id ?? selectedOffer.value.selected_offer_id ?? selectedOffer.value.id;
  },
  { immediate: true },
);

watch(
  () => [selectedOffer.value.id, selectedOffer.value.group_id, selectedOffer.value.selected_offer_id],
  () => {
    const nextSelection = getInitialMulticardSelection(selectedOffer.value);

    selectedGroupId.value = nextSelection.selectedGroupId;
    selectedValues.value = { ...nextSelection.selectedValues };
    selectedSizeCharts.value = { ...nextSelection.selectedSizeCharts };
  },
);

function activeValues(dimension: MulticardVariantDimension): MulticardVariantDimensionValue[] {
  return getActiveDimensionValues(dimension, selectedSizeCharts.value).filter((value) => {
    return (
      isSelectedValue(dimension.property_vendor_id, value.property_value_vendor_id) ||
      isVariantValueInStock(combinations.value, null, dimension.property_vendor_id, value.property_value_vendor_id)
    );
  });
}

function isSelectedValue(propertyVendorId: string, propertyValueVendorId: string): boolean {
  return selectedValues.value[propertyVendorId] === propertyValueVendorId;
}

function isPrimaryGroupAvailable(groupId: number): boolean {
  return combinations.value.some((combination) => {
    return (
      combination.group_id === groupId &&
      combination.balance > 0 &&
      Object.entries(selectedValues.value).every(([propertyVendorId, propertyValueVendorId]) => {
        return !propertyValueVendorId || combination.prop_vendor_ids[propertyVendorId] === propertyValueVendorId;
      })
    );
  });
}

function selectSizeChart(propertyVendorId: string, sizeChartId: number) {
  selectedSizeCharts.value = {
    ...selectedSizeCharts.value,
    [propertyVendorId]: sizeChartId,
  };
}

async function selectPrimaryGroup(groupId: number, uri: string) {
  const nextCombination = findBestInStockCombinationForPrimaryGroup(combinations.value, groupId, selectedValues.value);

  selectedGroupId.value = groupId;
  selectedValues.value = normalizeSelectedValues(
    nextCombination?.prop_vendor_ids ?? {},
    variantPropertyVendorIds.value,
  );
  selectedSizeId.value = nextCombination?.offer_id ?? null;

  const query = { ...route.query };

  if (nextCombination) {
    query.offer_id = String(nextCombination.offer_id);
  } else {
    delete query.offer_id;
  }

  await navigateToLatestSelection({
    path: uri,
    query,
  });
}

async function selectVariantValue(propertyVendorId: string, propertyValueVendorId: string) {
  const combination = findInStockCombinationForVariantValue(
    combinations.value,
    selectedGroupId.value,
    selectedValues.value,
    propertyVendorId,
    propertyValueVendorId,
  );

  if (!combination) {
    return;
  }

  selectedValues.value = normalizeSelectedValues(combination.prop_vendor_ids, variantPropertyVendorIds.value);
  selectedGroupId.value = combination.group_id;
  selectedSizeId.value = combination.offer_id;

  const targetPrimaryGroup = getPrimaryGroupById(primaryGroupsForDisplay.value, combination.group_id);
  const uri = targetPrimaryGroup?.uri ?? selectedOffer.value.uri;

  await navigateToLatestSelection({
    path: uri,
    query: {
      ...route.query,
      offer_id: combination.offer_id,
    },
  });
}
</script>

<template>
  <section class="product-page-multicard">
    <div
      v-if="primaryGroupsForDisplay.length"
      class="product-page-multicard__section"
    >
      <div class="product-page-multicard__header">
        <div class="product-page-multicard__title">{{ primaryGroupTitle }}</div>

        <div
          v-if="primarySizeChartPropertyVendorId && primarySizeCharts.length > 1"
          class="product-page-multicard__size-charts"
        >
          <button
            v-for="sizeChart in primarySizeCharts"
            :key="sizeChart.id"
            :class="{
              'product-page-multicard__size-chart--selected': sizeChart.id === selectedPrimarySizeChartId,
            }"
            class="product-page-multicard__size-chart"
            type="button"
            @click="selectSizeChart(primarySizeChartPropertyVendorId, sizeChart.id)"
          >
            {{ sizeChart.title }}
          </button>
        </div>
      </div>

      <div
        :class="{ 'product-page-multicard__options--tiles': hasPrimaryGroupThumbnails }"
        class="product-page-multicard__options"
      >
        <button
          v-for="group in primaryGroupsForDisplay"
          :key="group.id"
          :class="{
            'product-page-multicard__option--selected': group.id === selectedGroupId,
            'product-page-multicard__option--tile': hasPrimaryGroupThumbnails,
            'product-page-multicard__option--unavailable-combination': !isPrimaryGroupAvailable(group.id),
          }"
          class="product-page-multicard__option product-page-multicard__option--primary"
          type="button"
          @click="selectPrimaryGroup(group.id, group.uri)"
        >
          <span
            v-if="hasPrimaryGroupThumbnails"
            :class="{ 'product-page-multicard__thumbnail--empty': !primaryGroupThumbnailUrls[group.id] }"
            class="product-page-multicard__thumbnail"
          >
            <img
              v-if="primaryGroupThumbnailUrls[group.id]"
              :src="primaryGroupThumbnailUrls[group.id] ?? undefined"
              alt=""
            />

            <SvgoNoPhotoBig
              v-else
              :font-controlled="false"
              class="product-page-multicard__thumbnail-placeholder"
              filled
            />
          </span>

          <span class="product-page-multicard__option-label">
            {{ getActivePrimaryGroupLabel(group, selectedSizeCharts) }}
          </span>
        </button>
      </div>
    </div>

    <div
      v-for="dimension in variantDimensions"
      :key="dimension.property_vendor_id"
      class="product-page-multicard__section"
    >
      <div class="product-page-multicard__header">
        <div class="product-page-multicard__title">{{ dimension.name }}</div>

        <div
          v-if="dimension.size_charts?.length"
          class="product-page-multicard__size-charts"
        >
          <button
            v-for="sizeChart in dimension.size_charts"
            :key="sizeChart.id"
            :class="{
              'product-page-multicard__size-chart--selected':
                sizeChart.id === (selectedSizeCharts[dimension.property_vendor_id] ?? dimension.size_chart_id),
            }"
            class="product-page-multicard__size-chart"
            type="button"
            @click="selectSizeChart(dimension.property_vendor_id, sizeChart.id)"
          >
            {{ sizeChart.title }}
          </button>
        </div>
      </div>

      <div class="product-page-multicard__options">
        <button
          v-for="value in activeValues(dimension)"
          :key="value.property_value_vendor_id"
          :class="{
            'product-page-multicard__option--selected': isSelectedValue(
              dimension.property_vendor_id,
              value.property_value_vendor_id,
            ),
            'product-page-multicard__option--unavailable-combination': !isVariantValueAvailable(
              combinations,
              selectedGroupId,
              selectedValues,
              dimension.property_vendor_id,
              value.property_value_vendor_id,
            ),
          }"
          class="product-page-multicard__option"
          type="button"
          @click="selectVariantValue(dimension.property_vendor_id, value.property_value_vendor_id)"
        >
          {{ value.value }}
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.product-page-multicard {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__header {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    color: $black-60;
    font-size: $text-xs;
  }

  &__options,
  &__size-charts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__options--tiles {
    gap: 10px;
  }

  &__option,
  &__size-chart {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    min-height: 36px;
    padding: 6px 10px;
    border: 1px solid $black-20;
    border-radius: 8px;
    background-color: $white;
    color: $black;
    font-size: $text-sm;
    line-height: 1.2;
    transition:
      border-color 0.2s,
      color 0.2s,
      background-color 0.2s;
  }

  &__option--primary {
    transition:
      color 0.2s,
      background-color 0.2s;
  }

  &__option--tile {
    flex-direction: column;
    justify-content: space-between;
    width: 74px;
    min-height: 88px;
    padding: 5px;
    color: $black;
    font-size: $text-xs;
    text-align: center;

    @include screen-sm {
      width: 82px;
      min-height: 96px;
    }
  }

  &__option--tile &__option-label {
    display: -webkit-box;
    overflow: hidden;
    width: 100%;
    min-height: 2.4em;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  &__size-chart {
    min-height: 28px;
    padding: 4px 8px;
    font-size: $text-xs;
  }

  &__option:hover:not(:disabled),
  &__size-chart:hover:not(:disabled) {
    border-color: $black-60;
  }

  &__size-chart--selected {
    border-color: $black;
    background-color: $black;
    color: $white;
  }

  &__option--selected {
    border-color: #e84339;
    background-color: $white;
    color: $black;
    box-shadow: inset 0 0 0 1px #e84339;
  }

  &__option--primary:not(
      .product-page-multicard__option--selected,
      .product-page-multicard__option--unavailable-combination
    ) {
    border-color: $black-40;
    box-shadow: inset 0 0 0 1px $black-40;
  }

  &__option--unavailable-combination:not(.product-page-multicard__option--selected) {
    border-color: $black-10;
    background-color: $black-5;
    color: $black-40;
  }

  &__option--muted:not(.product-page-multicard__option--selected) {
    color: $black-40;
  }

  &__swatch {
    overflow: hidden;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: $black-5;

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  &__thumbnail {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    width: 100%;
    border-radius: 6px;
    background-color: $black-5;
    aspect-ratio: 1;

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
      padding: 4px;
      transition: transform 0.2s;
    }
  }

  &__thumbnail-placeholder {
    width: 28px;
    height: 28px;
    color: $black-40;
  }

  &__option--tile:hover:not(:disabled) &__thumbnail img {
    transform: scale(1.08);
  }
}
</style>
